import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
    UserRole,
    UserRoleLabelMapping,
} from '../../../../core/enums/users/role';
import { NavigationOptions } from '../../../../shared/components/page-header-nav/models/navigation-options';
import { PageHeaderNavComponent } from '../../../../shared/components/page-header-nav/page-header-nav.component';

import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MatAutocompleteModule } from '@angular/material/autocomplete';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { IMaskModule } from 'angular-imask';
import { Subject, takeUntil, tap } from 'rxjs';
import { ApiRouteConstants } from '../../../../core/constants/api-routes';
import { HtmlInputConstants } from '../../../../core/constants/html-input-constants';
import { IMaskConstants } from '../../../../core/constants/imask';
import { UserRoutes } from '../../../../core/constants/routes';
import { Contact } from '../../../../core/models/civi-crm/contact/contact';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { PhoneNumberService } from '../../../../core/services/phone-number/phone-number.service';
import { PostalCodeService } from '../../../../core/services/postal-code/postal-code.service';
import { PublicAppConfiguration } from '../../../../core/services/public-app-configuration/models/public-app-configuration';
import { PublicAppConfigurationService } from '../../../../core/services/public-app-configuration/public-app-configuration.service';
import { StateProvince } from '../../../../core/services/resources/models/state-province';
import { PhoneNumberInputComponent } from '../../../../shared/components/form-components/phone-number-input/phone-number-input.component';
import { StateProvinceInputComponent } from '../../../../shared/components/form-components/state-province-input/state-province-input.component';
import { UserRoleInputComponent } from '../../../../shared/components/form-components/user-role-input/user-role-input.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { MasqueradeService } from '../../shared/services/masquerade/masquerade.service';
import { MasqueradeRequest } from '../../shared/services/masquerade/models/masquerade-request';
import { UserStateService } from '../../shared/services/user-state/user-state.service';
import { UserProfileResponse } from '../shared/models/user-profile-response';
import { UserProfileService } from '../shared/services/user-profile/user-profile.service';
import { EditUserProfileMessage } from './constants/edit-user-profile-message';
import { MasqueradeModalConfig } from './constants/masquerade-modal-text';
import { UserProfile } from './models/user-profile';
import { UserProfileRequest } from './models/user-profile-request';
import { UserEmailVerificationService } from '../../shared/services/user-email-verification/user-email-verification.service';

@Component({
    selector: 'app-edit-account',
    imports: [
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        MatTooltipModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        RouterModule,
        PageHeaderNavComponent,
        MatDialogModule,
        MatButtonModule,
        MatSlideToggleModule,
        PhoneNumberInputComponent,
        StateProvinceInputComponent,
        UserRoleInputComponent,
        IMaskModule,
    ],
    templateUrl: './edit-user-profile.component.html',
})
export class EditUserProfileComponent implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private userProfileService = inject(UserProfileService);
    private readonly dialog = inject(MatDialog);
    private readonly alertService = inject(AlertService);
    private readonly masqueradeService = inject(MasqueradeService);
    private readonly userStateService = inject(UserStateService);
    private readonly phoneNumberService = inject(PhoneNumberService);
    private readonly postalCodeService = inject(PostalCodeService);
    private readonly publicAppConfigurationService = inject(
        PublicAppConfigurationService,
    );
    private readonly userEmailVerificationService = inject(
        UserEmailVerificationService,
    );

    organizationMatch = false;
    isAvailable: boolean = true;
    originalOrganizationName = '';
    isEmailVerified = false;

    form: FormGroup<FormGroupType<UserProfile>>;

    profileResponse: UserProfileResponse | undefined;

    user: UserProfile = {} as UserProfile;

    userRoles: UserRole[] = Object.values(UserRole).filter(
        (role) => typeof role === 'number',
    ) as UserRole[];
    stateProvinces: StateProvince[] = [];

    maskOptions = IMaskConstants;
    regExPattern = RegExPattern;
    htmlInputConstants = HtmlInputConstants;

    private userId: string | null = this.route.snapshot.paramMap.get('id');
    private hasChanges = false;
    private destroy$ = new Subject<void>();

    readonly UserRoleLabelMapping = UserRoleLabelMapping;

    pageHeaderNavItems: NavigationOptions = {};

    constructor() {
        this.form = new FormGroup<FormGroupType<UserProfile>>({
            username: new FormControl({ value: '', disabled: true }),
            role: new FormControl(undefined, Validators.required),
            firstName: new FormControl('', Validators.required),
            lastName: new FormControl('', Validators.required),
            email: new FormControl({ value: '', disabled: true }),
            jobTitle: new FormControl('', Validators.required),
            streetAddress: new FormControl('', Validators.required),
            city: new FormControl('', Validators.required),
            stateProvinceId: new FormControl('', Validators.required),
            postalCode: new FormControl('', Validators.required),
            phoneNumber: new FormControl('', Validators.required),
            isActive: new FormControl(undefined),
        });
    }

    async ngOnInit() {
        if (this.userId) {
            this.userProfileService
                .getById(+this.userId)
                .then((profileResponse: UserProfileResponse) => {
                    this.profileResponse = profileResponse;

                    this.form.patchValue({
                        username: profileResponse.user.username,
                        role: profileResponse.user.role,
                        firstName: profileResponse.contact.firstName,
                        lastName: profileResponse.contact.lastName,
                        email: profileResponse.user.email,
                        jobTitle: profileResponse.contact.jobTitle,
                        streetAddress:
                            profileResponse.contact.address.streetAddress,
                        city: profileResponse.contact.address.city,
                        stateProvinceId:
                            profileResponse.contact.address.stateProvince?.toString(),
                        postalCode:
                            this.postalCodeService.buildPostalCodeString(
                                profileResponse.contact.address.postalCode,
                                profileResponse.contact.address
                                    .postalCodeSuffix,
                            ),
                        phoneNumber:
                            this.phoneNumberService.buildPhoneNumberForInput(
                                profileResponse.contact.phone.phoneNumber,
                                profileResponse.contact.phone.phoneExtension,
                            ),
                        isActive: profileResponse.user.isActive,
                    });

                    // ngx-mask dirties the form when writing into masked controls; restore
                    // pristine so loading the profile isn't treated as a user edit.
                    this.form.markAsPristine();

                    this.form.valueChanges
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(() => {
                            this.hasChanges = this.form.dirty;
                        });

                    this.publicAppConfigurationService
                        .getOrRefreshApplicationConfiguration()
                        .pipe(
                            tap(
                                (
                                    publicAppConfiguration: PublicAppConfiguration,
                                ) => {
                                    // Setting the page header navigation items based on the public app configuration
                                    this.setPageHeaderNavItems(
                                        publicAppConfiguration,
                                        profileResponse.contact,
                                    );
                                },
                            ),
                            tap(() => {
                                // Once the page header navigation items are set, check if the masquerade button should be added
                                this.checkAddMasqueradeButton();
                            }),
                            takeUntil(this.destroy$),
                        )
                        .subscribe();

                    this.originalOrganizationName =
                        profileResponse.organization.name ?? '';
                });
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    saveChanges() {
        if (this.form.valid) {
            this.hasChanges = false;

            const { phoneNumber, phoneNumberExtension } =
                this.phoneNumberService.getPhoneNumberAndExtension(
                    this.form.value.phoneNumber!,
                );

            /* eslint-disable  @typescript-eslint/no-unused-vars */
            const { username, email, ...requestData } = this.form.value;

            // Implement save logic here
            const request: UserProfileRequest = {
                ...requestData,
                phoneNumber: phoneNumber,
                phoneNumberExtension: phoneNumberExtension,
            } as UserProfileRequest;

            this.userProfileService
                .updateById(+this.userId!, request)
                .then(() => {
                    this.alertService.displaySuccessAlert(
                        EditUserProfileMessage.USER_CHANGES_SAVED,
                    );

                    this.router.navigate([`/${UserRoutes.getRootUrl()}`]);
                })
                .catch((error) => {
                    console.error('Error updating profile:', error);
                });
        } else {
            // Show validation errors
            this.form.markAllAsTouched();
        }
    }

    cancel() {
        // Navigate back or show confirmation dialog
        this.router.navigate([`/${UserRoutes.getRootUrl()}`]);
    }

    openMasqueradeModel(): void {
        const fullName: string = `${this.form.value.firstName} ${this.form.value.lastName}`;

        const dialogConfig = MasqueradeModalConfig.getDialogConfig(
            fullName,
            this.masquerade.bind(this),
        );

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: dialogConfig,
        });
    }

    masquerade(): void {
        const request: MasqueradeRequest = {
            masqueradeAsUserId: this.profileResponse!.user.id!,
        };

        this.masqueradeService.masqueradeAsUser(request);
    }

    sendVerificationEmail(): void {
        const userId = this.profileResponse?.user.id;
        if (!userId) {
            return;
        }

        this.userEmailVerificationService.sendVerificationEmail(userId);
    }

    private checkAddMasqueradeButton() {
        if (
            !this.isCurrentUsersProfile() &&
            !this.isUserAlreadyMasqueraded() &&
            this.isMasqueradeAllowedForRole() &&
            this.isProfileActive()
        ) {
            this.addMasqueradeButton();
        }
    }

    private isProfileActive(): boolean {
        return this.form.controls.isActive?.value === true;
    }

    private isUserAlreadyMasqueraded(): boolean {
        return !!this.userStateService.getUser()?.isMasqueraded;
    }

    private isMasqueradeAllowedForRole(): boolean {
        const userRole = this.profileResponse?.user.role;

        return userRole
            ? this.masqueradeService.shouldShowMasqueradeButton(userRole)
            : false;
    }

    /**
     * Check whether the user is on their own profile. A user cannot masquerade as themselves.
     * @returns
     */
    private isCurrentUsersProfile(): boolean {
        const currentUser = this.userStateService.getUser();

        return (
            currentUser !== null &&
            currentUser.username.toLowerCase() ===
                this.profileResponse?.user.username.toLowerCase()
        );
    }

    private addMasqueradeButton() {
        this.pageHeaderNavItems.quaternaries!.unshift({
            title: 'Masquerade',
            classList: '!rounded-lg !bg-error-700 !text-tertiary-50',
            imageUrl: 'assets/svg/masquerade_light.svg',
            action: this.openMasqueradeModel.bind(this),
        });
    }

    private setPageHeaderNavItems(
        publicAppConfiguration: PublicAppConfiguration,
        contact: Contact,
    ) {
        this.pageHeaderNavItems = {
            quaternaries: [
                {
                    title: 'View in CiviCRM',
                    classList: '!rounded-lg !border-primary-700',
                    action: () => {
                        window.open(
                            ApiRouteConstants.CiviCrm.getContactUrl(
                                publicAppConfiguration.civiCrmUrl,
                                contact.id,
                            ),
                            '_blank',
                        );
                    },
                },
            ],
            tertiaries: [
                {
                    title: 'Cancel',
                    classList: '!rounded-lg !bg-tertiary-700 !text-tertiary-50',
                    action: this.cancel.bind(this),
                },
            ],
            primaries: [
                {
                    title: 'Save Changes',
                    classList: '!rounded-lg',
                    action: this.saveChanges.bind(this),
                },
            ],
        };
    }
}
